import React, { useEffect } from 'react'
import {
	Container,
	Flex,
	Text,
	Button,
	Box,
	useDisclosure,
} from '@chakra-ui/react'
import tenant from '@/lib/tenant'

// TypeScript declarations for window objects
declare global {
	// eslint-disable-next-line no-unused-vars
	interface Window {
		posthog?: any
		dataLayer?: any[]
	}
}

// Was a hardcoded Commsure PostHog project key/host, duplicated in both
// handleAccept() and the "consent already given" effect below — every other
// tenant silently shipped Commsure's own analytics key. Same env vars
// pages/_app.tsx reads, so a tenant with no PostHog project configured just
// has no analytics (no init call at all) instead of a hardcoded fallback.
function initPostHogIfConfigured() {
	const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY
	if (!posthogKey) return
	if (typeof globalThis === 'undefined' || globalThis.posthog?.__loaded) return

	const posthog = require('posthog-js').default
	posthog.init(posthogKey, {
		api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
		person_profiles: 'always',
	})
}

export const CookieConsent = () => {
	const { isOpen, onOpen, onClose } = useDisclosure()

	useEffect(() => {
		const cookie = localStorage.getItem('cookieConsent')

		if (!cookie) {
			// No consent recorded - show banner
			onOpen()
		} else if (cookie === 'true') {
			// User previously accepted - initialize PostHog
			initPostHogIfConfigured()
		}
	}, [onOpen])

	function handleReject() {
		// User rejected cookies - store preference
		localStorage.setItem('cookieConsent', 'false')

		// Opt out of PostHog if it was already initialized
		if (typeof globalThis !== 'undefined' && globalThis.posthog) {
			globalThis.posthog.opt_out_capturing()
		}

		onClose()
	}

	function handleAccept() {
		// User accepted cookies - store preference
		localStorage.setItem('cookieConsent', 'true')

		if (typeof globalThis !== 'undefined') {
			initPostHogIfConfigured()

			// Trigger GTM consent event if GTM is loaded
			if (globalThis.dataLayer) {
				globalThis.dataLayer.push({ event: 'cookie_consent_granted' })
			}

			// Dispatch custom event to notify other components
			globalThis.dispatchEvent(new Event('cookieConsentAccepted'))
		}

		onClose()
	}

	return (
		<Container
			display={isOpen ? 'block' : 'none'}
			maxW='container.md'
			position='fixed'
			bottom={4}
			left={0}
			right={0}
			zIndex='sticky'
		>
			<Flex
				justifyContent='space-between'
				bg='white'
				alignItems={{ base: 'flex-start', md: 'center' }}
				shadow='lg'
				px={4}
				py={3}
				borderRadius='lg'
				gap={3}
				flexDir={{ base: 'column', md: 'row' }}
			>
				<Box fontSize='sm'>
					<Text fontWeight='bold'>Cookie consent</Text>
					<Text>{tenant.copy.cookieConsentBody}</Text>
				</Box>
				<Flex gap={3}>
					<Button size='sm' borderRadius='full' onClick={handleReject}>
						Reject
					</Button>
					<Button
						size='sm'
						borderRadius='full'
						variant='solid'
						order={{ base: -1, md: 0 }}
						onClick={handleAccept}
					>
						Accept
					</Button>
				</Flex>
			</Flex>
		</Container>
	)
}
